/**
 * Pub/sub for "the auth session was just adopted in place".
 *
 * When a guest signs up / logs in through the IN-APP modal (the IDE's
 * AuthModalMount or the Dashboard's GlobalAuthModal), the tab keeps running —
 * there is no page reload to re-fetch data that differs between the guest and the
 * now-authenticated user. {@link adoptLoginSession} claims the guest's projects
 * server-side, then fires {@link notifySessionAdopted} so any mounted view
 * showing guest-vs-user data (the Dashboard project list, …) re-fetches at once
 * instead of showing stale guest state until a manual reload.
 *
 * Decoupled pub/sub (mirrors `onUsageInvalidated`) so the producer
 * (adoptLoginSession) and the consumers (page components) need not know about
 * each other.
 *
 * @module
 */

/** Listeners notified once a fresh session has been adopted + guest projects claimed. */
const sessionAdoptedListeners = new Set<() => void>()

/**
 * Subscribe to session-adopted events so a mounted view can re-fetch the data
 * that changes when a guest becomes an authenticated user (e.g. the project list).
 *
 * @param listener - Called after an in-place login has been adopted.
 * @returns An unsubscribe function.
 */
export function onSessionAdopted(listener: () => void): () => void {
  sessionAdoptedListeners.add(listener)
  return () => {
    sessionAdoptedListeners.delete(listener)
  }
}

/**
 * Notify all subscribers that a fresh session was adopted in place and their
 * user-scoped data should be re-fetched. A listener that throws is isolated so it
 * cannot break the producer or its peers.
 */
export function notifySessionAdopted(): void {
  for (const listener of sessionAdoptedListeners) {
    try {
      listener()
    } catch (_error) {
      // A misbehaving subscriber must not break the producer or other listeners.
    }
  }
}
